import React, { useEffect, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { SetDynamic as BLSetDynamic } from './../../../utilities/BL/Admin/Dashboard/Dynamics';
import {
    GetSettings,
    GetAdminParticipants,
    SelectWinner,
    ClearWinner,
    UpdatePosition,
    UpdateBannerMode,
    UpdateOverlayVisibility,
    GetAdminStats,
    ResetGame,
    ConfigGame,
    ToggleActiveGame,
    ShowOverlay,
    HideOverlay,
    RefreshOverlay
} from './../../../utilities/BL/Admin/Games/PluckingRooster';
import { RoosterThemeModal } from './../Games/RoosterThemeModal';

const formatMexicoDate = (dateString) => {
    if (!dateString) return 'No disponible';
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }) + ' hrs';
};

function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

const PluckingRooster = ({ getOutOfTheDynamic, roomId }) => {
    const { showMessage } = useFloatMessage();

    const [participants, setParticipants] = useState([]);
    const [stats, setStats] = useState({
        qr_scan: 0,
        page_view: 0,
        form_start: 0,
        form_complete: 0,
        game_start: 0,
        game_complete: 0,
        whatsapp_click: 0
    });
    const [isLoading, setIsLoading] = useState(false);
    const [duration, setDuration] = useState(15);
    
    // Live Overlay State
    const [offsetY, setOffsetY] = useState(0);
    const [bannerMode, setBannerMode] = useState('invitation'); // invitation, winner, ranking
    const [winnerName, setWinnerName] = useState('');
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [isGameActive, setIsGameActive] = useState(true);

    // Leyenda y búsqueda
    const [lastUpdated, setLastUpdated] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [winnerFilter, setWinnerFilter] = useState('all');

    // Colapsabilidad de paneles
    const [isStadiumPanelExpanded, setIsStadiumPanelExpanded] = useState(false);
    const [isDangerZonePanelExpanded, setIsDangerZonePanelExpanded] = useState(false);

    // Modales de confirmación y personalización
    const [showConfirm, setShowConfirm] = useState(false);
    const [, setConfirmData] = useState(null);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        action: null,
        textYes: 'Sí, confirmar',
        textNo: 'Cancelar'
    });
    const [showThemeModal, setShowThemeModal] = useState(false);

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const throttledUpdatePosition = useRef(
        throttle((val) => {
            UpdatePosition(val, roomId);
        }, 80)
    ).current;

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            // 1. Participantes
            const res = await GetAdminParticipants(roomId);
            if (res.success && res.data) {
                setParticipants(res.data);
            }

            // 2. Estadísticas
            const statsRes = await GetAdminStats(roomId);
            if (statsRes.success && statsRes.data) {
                setStats(statsRes.data);
            }

            // 3. Configuración del Overlay
            const settingsRes = await GetSettings(roomId);
            if (settingsRes.success && settingsRes.data) {
                setOffsetY(settingsRes.data.offsetY ?? 0);
                setBannerMode(settingsRes.data.bannerMode || 'invitation');
                setWinnerName(settingsRes.data.winnerName || '');
                setIsOverlayVisible(settingsRes.data.isOverlayVisible ?? false);
                setIsGameActive(settingsRes.data.isGameActive ?? true);
            }

            setLastUpdated(new Date().toLocaleString('es-MX', { hour12: false }) + ' hrs');
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: error.message || 'Error al cargar la información del panel.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId]);

    // --- ACCIONES DE GANADOR ---

    const triggerSelectWinner = (id, name) => {
        setConfirmConfig({
            message: id === 0 
                ? '¿Estás seguro de que deseas desmarcar al ganador actual?' 
                : `¿Estás seguro de que deseas elegir a "${name}" como el ganador de la dinámica?`,
            textYes: id === 0 ? 'Sí, desmarcar' : 'Sí, elegir ganador',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: id === 0 ? 'Desmarcando ganador...' : 'Estableciendo ganador...' });
                let res;
                if (id === 0) {
                    const currentW = participants.find(p => p.isWinner);
                    res = await ClearWinner(currentW ? currentW.id : 0, roomId);
                } else {
                    res = await SelectWinner(id, roomId);
                }

                if (res.success) {
                    setParticipants(prev => 
                        prev.map(p => ({ ...p, isWinner: p.id === id }))
                    );
                    setWinnerName(id === 0 ? '' : name);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: id === 0 ? 'Ganador desmarcado correctamente.' : `¡Felicidades! ${name} ha sido elegido como ganador.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar ganador' });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const triggerResetGame = () => {
        setConfirmConfig({
            message: '¿Estás segur@ de que deseas reiniciar la dinámica por completo? Esto eliminará de forma irreversible a todos los participantes registrados y las métricas en el servidor.',
            textYes: 'Sí, reiniciar todo',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando dinámica...' });
                const res = await ResetGame(roomId);
                if (res.success) {
                    setParticipants([]);
                    setWinnerName('');
                    setIsOverlayVisible(false);
                    setBannerMode('invitation');
                    setOffsetY(0);
                    setStats({
                        qr_scan: 0,
                        page_view: 0,
                        form_start: 0,
                        form_complete: 0,
                        game_start: 0,
                        game_complete: 0,
                        whatsapp_click: 0
                    });
                    setLastUpdated(new Date().toLocaleString('es-MX', { hour12: false }) + ' hrs');
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: '¡La dinámica ha sido reiniciada por completo!'
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al reiniciar el juego' });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const triggerBannerModeChange = (mode) => {
        if (mode === 'winner' && (!winnerNameShow || winnerNameShow.trim() === '')) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Aún no se ha seleccionado a un ganador.'
            });
            return;
        }

        setConfirmConfig({
            message: `¿Deseas cambiar el banner al modo "${mode === 'invitation' ? 'Invitación / QR' : mode === 'winner' ? 'Ganador' : 'Ranking'}"?`,
            textYes: 'Sí, cambiar modo',
            textNo: 'Cancelar',
            action: async () => {
                const res = await UpdateBannerMode(mode, roomId);
                if (res.success) {
                    setBannerMode(mode);
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Modo del banner actualizado.' });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al cambiar modo' });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const triggerToggleOverlayVisibility = (nextState) => {
        setConfirmConfig({
            message: `¿Estás segur@ de que deseas ${nextState ? 'mostrar' : 'ocultar'} el banner overlay en la transmisión?`,
            textYes: nextState ? 'Sí, mostrar' : 'Sí, ocultar',
            textNo: 'Cancelar',
            action: async () => {
                const res = await UpdateOverlayVisibility(nextState, roomId);
                if (res.success) {
                    setIsOverlayVisible(nextState);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `El overlay ahora está ${nextState ? 'visible' : 'oculto'}.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al cambiar visibilidad' });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const triggerToggleActive = (nextState) => {
        setConfirmConfig({
            message: `¿Estás segur@ de que deseas ${nextState ? 'ABRIR' : 'CERRAR'} los registros y el acceso al juego para los aficionados?`,
            textYes: nextState ? 'Sí, abrir registros' : 'Sí, cerrar registros',
            textNo: 'Cancelar',
            action: async () => {
                const res = await ToggleActiveGame(nextState, roomId);
                if (res.success) {
                    setIsGameActive(nextState);
                    showMessage({ 
                        type: MESSAGE_TYPE.SUCCESS, 
                        text: nextState ? 'Registros del juego abiertos.' : 'Registros del juego cerrados.' 
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al cambiar estado' });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const handleConfigGame = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando tiempo del juego...' });
        const res = await ConfigGame(duration, roomId);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Duración de juego configurada a ${duration} segundos.` });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al guardar duración' });
        }
    };

    const handleShowOverlay = async () => {
        const res = await ShowOverlay(10, roomId);
        if (res.success) {
            setIsOverlayVisible(true);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ranking mostrado en pantalla y overlay.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al mostrar ranking en pantalla.' });
        }
    };

    const handleHideOverlay = async () => {
        const res = await HideOverlay(roomId);
        if (res.success) {
            setIsOverlayVisible(false);
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Ranking ocultado en pantallas.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al ocultar ranking.' });
        }
    };

    const handleRefreshOverlay = async () => {
        const res = await RefreshOverlay(10, roomId);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Datos actualizados en pantallas y overlay.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al refrescar ranking.' });
        }
    };

    const showQrOnScreen = async () => {
        try {
            await BLSetDynamic(19); // ID 19: Código QR Aplasta moritas / Gallo
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'QR mostrado en pantalla gigante' });
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se pudo enviar la instrucción a pantalla' });
        }
    };

    const showRankingOnScreen = async () => {
        try {
            await BLSetDynamic(21); // ID 21: Ranking Aplasta moritas / Gallo
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ranking mostrado en pantalla gigante' });
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se pudo enviar la instrucción a pantalla' });
        }
    };

    const downloadExcelReport = () => {
        if (participants.length === 0) {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'No hay registros para exportar.' });
            return;
        }

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte de Excel...' });

        const sortedParticipants = [...participants].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        const dataToExport = sortedParticipants.map((p) => ({
            'Fecha de participación': formatMexicoDate(p.createdAt),
            Nombre: p.fullName || p.name,
            Zona: p.companionType === 'azul' ? 'Zona Azul' : p.companionType === 'oro' ? 'Zona Oro' : p.companionType || 'No seleccionada',
            Correo: p.email,
            'WhatsApp / Teléfono': p.phoneNumber || p.phone,
            'Puntuación (Pts)': p.score,
            '¿Es Ganador(a)?': p.isWinner ? 'Sí' : ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Participantes');

        const dateStr = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `Ranking_Pegale_Al_Personaje_${dateStr}.xlsx`);

        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Excel generado y descargado correctamente.' });
    };

    const handleReturn = () => {
        if (getOutOfTheDynamic) {
            getOutOfTheDynamic();
        } else {
            window.location.href = '/admin/dashboard';
        }
    };

    // --- FILTRADO Y PAGINACIÓN (CLIENT SIDE) ---
    const filteredParticipants = participants.filter(p => {
        const nameText = p.fullName || p.name || '';
        const phoneText = p.phoneNumber || p.phone || '';
        const emailText = p.email || '';

        const matchesSearch = !searchTerm || 
            nameText.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emailText.toLowerCase().includes(searchTerm.toLowerCase()) ||
            phoneText.includes(searchTerm);
            
        const matchesWinner = winnerFilter === 'all' || 
            (winnerFilter === 'winner' && p.isWinner) ||
            (winnerFilter === 'non-winner' && !p.isWinner);

        return matchesSearch && matchesWinner;
    });

    const totalRegistries = filteredParticipants.length;
    const totalPages = Math.ceil(totalRegistries / pageSize) || 1;
    const verifiedPage = Math.min(currentPage, totalPages);
    const startIndex = (verifiedPage - 1) * pageSize;
    const paginatedParticipants = filteredParticipants.slice(startIndex, startIndex + pageSize);

    const winnerDetails = participants.find(p => p.isWinner);
    const winnerNameShow = winnerDetails ? (winnerDetails.fullName || winnerDetails.name) : winnerName;

    return (
        <SecondaryBase actionToExecute={handleReturn}>
            {showConfirm && (
                <Confirm 
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                    setConfirmData={setConfirmData}
                />
            )}

            {/* MODAL DE PERSONALIZACIÓN DE TEMA */}
            <RoosterThemeModal 
                show={showThemeModal} 
                onClose={() => setShowThemeModal(false)} 
                roomId={roomId}
                onApply={() => loadInitialData()}
                onSave={() => loadInitialData()}
            />

            <div className="container-fluid px-4 py-3 OverlayQuizzBoard">
                
                {/* CABECERA PRINCIPAL */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 border-bottom pb-3 gap-3">
                    <div>
                        <h1 className="h3 fw-bold text-dark mb-1">
                            <i className="bi bi-controller text-danger me-2"></i> 
                            Pégale al Personaje (Plucking Rooster)
                        </h1>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-warning-subtle text-dark border border-warning-subtle fw-semibold">
                                Consola de Operador SaaS
                            </span>
                            {roomId && (
                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle fw-semibold">
                                    <i className="bi bi-door-open-fill me-1"></i> Sala ID: {roomId}
                                </span>
                            )}
                        </div>
                    </div>
                    <div>
                        <button 
                            type="button" 
                            className="btn btn-warning fw-bold shadow-sm text-dark d-flex align-items-center gap-2 px-3 py-2"
                            onClick={() => setShowThemeModal(true)}
                        >
                            <i className="bi bi-palette-fill fs-5"></i>
                            Personalizar Temática
                        </button>
                    </div>
                </div>

                {/* 1. Botón "Mostrar QR en pantalla gigante" */}
                <div className="mb-4">
                    <button 
                        type="button" 
                        onClick={showQrOnScreen} 
                        className="btn btn-outline-primary btn-lg w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                    >
                        <i className="bi bi-qr-code-scan fs-4"></i>
                        Mostrar QR en pantalla
                    </button>
                </div>

                <div className="row g-4 mb-4">
                    {/* CONFIGURACIÓN DEL OVERLAY */}
                    <div className="col-12">
                        <div className="bg-light p-4 rounded-3 border h-100 shadow-sm">
                            <p className='fs-4 fw-bold text-center mb-3 text-dark'>Control de Overlay en Transmisión</p>

                            <div className="mb-4">
                                <p className='text-center fs-5 text-dark'>
                                    Estatus Overlay: <span className={`fw-bold ${isOverlayVisible ? 'text-success' : 'text-danger'}`}>{isOverlayVisible ? 'Visible en OBS' : 'Oculto en OBS'}</span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => triggerToggleOverlayVisibility(!isOverlayVisible)}
                                    className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                        isOverlayVisible 
                                        ? 'btn-outline-danger' 
                                        : 'btn-outline-success'
                                    }`}
                                >
                                    <i className={`bi ${isOverlayVisible ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                    {isOverlayVisible ? 'Ocultar banner' : 'Mostrar banner'}
                                </button>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fw-semibold text-dark text-center small d-block mb-2">
                                    Selecciona la opción que desees mostrar:
                                </label>
                                <div className="btn-group w-100" role="group">
                                    <button 
                                        type="button" 
                                        onClick={() => triggerBannerModeChange('invitation')}
                                        className={`btn py-2.5 fw-bold ${bannerMode === 'invitation' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    >
                                        {bannerMode === 'invitation' && <i className="bi bi-check-circle-fill me-2"></i>}
                                        <i className="bi bi-qr-code me-2"></i>
                                        Mostrar QR para escanear
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => triggerBannerModeChange('winner')}
                                        className={`btn py-2.5 fw-bold ${bannerMode === 'winner' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    >
                                        {bannerMode === 'winner' && <i className="bi bi-check-circle-fill me-2"></i>}
                                        <i className="bi bi-trophy-fill me-2"></i>
                                        Mostrar ganador
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => triggerBannerModeChange('ranking')}
                                        className={`btn py-2.5 fw-bold ${bannerMode === 'ranking' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    >
                                        {bannerMode === 'ranking' && <i className="bi bi-check-circle-fill me-2"></i>}
                                        <i className="bi bi-list-ol me-2"></i>
                                        Mostrar tabla ranking
                                    </button>
                                </div>
                            </div>
                            
                            <span className="d-block text-center text-dark small fw-bold mb-2">Vista previa simulada:</span>
                            <div className="bg-black text-white p-3 rounded-2 text-center small mb-3 d-flex align-items-center justify-content-center shadow-inner" style={{ minHeight: '65px' }}>
                                {bannerMode === 'invitation' ? (
                                    <div className="d-flex justify-content-around align-items-center w-100">
                                        <i className="bi bi-qr-code"></i>
                                        <span className="fw-semibold">Escanea el QR y participa en Pégale al Personaje</span>
                                        <i className="bi bi-qr-code"></i>
                                    </div>
                                ) : bannerMode === 'winner' ? (
                                    <div>
                                        <span className="d-block text-warning fw-bold text-uppercase" style={{ fontSize: '0.65rem' }}>🎉 Ganador Oficial 🎉</span>
                                        <span className="fw-bold fs-6">{winnerNameShow || '(Ninguno seleccionado)'}</span>
                                    </div>
                                ) : (
                                    <div>
                                        <span className="d-block text-info fw-bold text-uppercase" style={{ fontSize: '0.65rem' }}>📊 Tabla de Ranking 📊</span>
                                        <span className="small">Top 5 mejores puntuaciones visibles en OBS</span>
                                    </div>
                                )}
                            </div>

                            {/* Tarjeta del Ganador */}
                            <div className={`card p-3 border-0 rounded-3 shadow-sm ${winnerNameShow ? 'bg-warning-subtle text-warning-emphasis' : 'bg-white text-secondary'} text-center`}>
                                <div className="d-flex justify-content-center align-items-center flex-wrap gap-2">
                                    <i className={`bi ${winnerNameShow ? 'bi-trophy-fill fs-4 text-warning' : 'bi-exclamation-triangle-fill fs-5'}`}></i>
                                    {winnerDetails ? (
                                        <span className="fw-bold">
                                            Ganador seleccionado: {winnerDetails.fullName || winnerDetails.name} ({winnerDetails.email} / {winnerDetails.phoneNumber || winnerDetails.phone}) — {winnerDetails.score} pts
                                        </span>
                                    ) : winnerNameShow ? (
                                        <span className="fw-bold">Ganador seleccionado: {winnerNameShow}</span>
                                    ) : (
                                        <span>No se ha seleccionado ningún ganador todavía. Haz clic en "Elegir Ganador" en la lista inferior.</span>
                                    )}

                                    {winnerNameShow && (
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-danger fw-bold ms-2 py-1 px-3 shadow-sm"
                                            onClick={() => triggerSelectWinner(0, '')}
                                        >
                                            <i className="bi bi-x-circle-fill me-1"></i> Desmarcar ganador
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ESTADÍSTICAS EN TIEMPO REAL */}
                <p className='fs-4 text-center mt-5 mb-1 fw-bold text-dark'>Estadísticas en Tiempo Real</p>
                <div className="text-center mb-3">
                    <button 
                        type="button" 
                        className="btn btn-outline-primary mb-1 fw-bold shadow-sm"
                        onClick={loadInitialData}
                        disabled={isLoading}
                    >
                        <i className={`bi bi-arrow-clockwise me-1 ${isLoading ? 'spin' : ''}`}></i> Recargar estadísticas y listado
                    </button>
                    {lastUpdated && (
                        <div className="text-muted small mt-1">
                            <i className="bi bi-clock-history me-1"></i> Última actualización: {lastUpdated}
                        </div>
                    )}
                </div>

                <div className="row g-3 mb-4 text-center">
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-qr-code text-danger fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Escaneos QR</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.qr_scan ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-globe text-primary fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Ingresos Página</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.page_view ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-pencil-square text-warning fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Inicios Registro</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.form_start ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-check-circle-fill text-success fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Registros Completos</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.form_complete ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-play-fill text-info fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Juegos Iniciados</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.game_start ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-flag-fill text-success fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Juegos Terminados</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.game_complete ?? 0}</div>
                        </div>
                    </div>
                </div>

                {/* LISTADO DE PARTICIPANTES */}
                <div className="bg-white p-4 rounded-3 shadow-sm border mt-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3 pb-3 border-bottom">
                        <h2 className="fs-5 fw-bold text-dark mb-0">
                            Listado General de Participantes ({totalRegistries} encontrados)
                        </h2>
                        <button 
                            type="button" 
                            onClick={downloadExcelReport}
                            className="btn btn-outline-success fw-bold d-flex align-items-center gap-2 shadow-sm"
                        >
                            <i className="bi bi-file-earmark-excel-fill"></i>
                            Exportar datos a Excel
                        </button>
                    </div>

                    {/* Buscador y filtros */}
                    <div className="row g-3 mb-4">
                        <div className="col-12 col-md-5">
                            <div className="input-group shadow-sm">
                                <span className="input-group-text bg-light"><i className="bi bi-search text-secondary"></i></span>
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="Buscar por nombre, correo o WhatsApp..." 
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                />
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <select 
                                className="form-select shadow-sm"
                                value={winnerFilter}
                                onChange={(e) => { setWinnerFilter(e.target.value); setCurrentPage(1); }}
                            >
                                <option value="all">Todos los participantes</option>
                                <option value="winner">Ganadores seleccionados</option>
                                <option value="non-winner">No ganadores</option>
                            </select>
                        </div>
                        {/* Selector de registros por página */}
                        <div className="col-12 col-md-3">
                            <div className="input-group shadow-sm">
                                <span className="input-group-text bg-light fw-bold small text-secondary">Mostrar</span>
                                <select 
                                    className="form-select"
                                    value={pageSize}
                                    onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                                >
                                    <option value={10}>10 registros</option>
                                    <option value={25}>25 registros</option>
                                    <option value={50}>50 registros</option>
                                    <option value={100}>100 registros</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Tabla con scroll vertical automático y alto de 500px */}
                    <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        <table className="table table-hover align-middle table-bordered-rows">
                            <thead className="table-light text-secondary small uppercase fw-bold sticky-top bg-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: '5%' }}>#</th>
                                    <th style={{ width: '20%' }}>Nombre Completo</th>
                                    <th style={{ width: '10%' }}>Zona</th>
                                    <th style={{ width: '20%' }}>Correo Electrónico</th>
                                    <th style={{ width: '15%' }}>WhatsApp / Teléfono</th>
                                    <th style={{ width: '15%' }}>Fecha de Participación</th>
                                    <th style={{ width: '5%' }} className="text-center">Score</th>
                                    <th style={{ width: '10%' }} className="text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedParticipants.map((p, index) => {
                                    const realIndex = startIndex + index + 1;
                                    const isW = p.isWinner;

                                    return (
                                        <tr key={p.id || index} className={isW ? 'table-warning fw-semibold' : ''}>
                                            <td className="text-secondary small">{realIndex}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    {isW && <i className="bi bi-trophy-fill text-warning fs-5"></i>}
                                                    <span>{p.fullName || p.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                {p.companionType === 'azul' ? (
                                                    <span className="badge bg-primary text-white text-uppercase" style={{ fontSize: '0.75rem' }}>Azul</span>
                                                ) : p.companionType === 'oro' ? (
                                                    <span className="badge bg-warning text-dark text-uppercase" style={{ fontSize: '0.75rem' }}>Oro</span>
                                                ) : (
                                                    <span className="text-muted small italic">{p.companionType || 'N/A'}</span>
                                                )}
                                            </td>
                                            <td className="text-secondary">{p.email}</td>
                                            <td>{p.phoneNumber || p.phone}</td>
                                            <td className="text-secondary small">{formatMexicoDate(p.createdAt)}</td>
                                            <td className="text-center fw-bold text-danger">{p.score} pts</td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">
                                                    {isW ? (
                                                        <button 
                                                            type="button" 
                                                            onClick={() => triggerSelectWinner(0, '')}
                                                            className="btn btn-sm btn-danger fw-bold py-1.5 px-3 shadow-sm"
                                                        >
                                                            Desmarcar
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            type="button" 
                                                            onClick={() => triggerSelectWinner(p.id, p.fullName || p.name)}
                                                            className="btn btn-sm btn-outline-warning text-dark fw-bold py-1.5 px-3 shadow-sm"
                                                        >
                                                            Elegir Ganador
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {paginatedParticipants.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="text-center py-4 text-muted fs-5">
                                            No se encontraron participantes con los filtros aplicados.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                            <span className="text-secondary small">
                                Mostrando del {startIndex + 1} al {Math.min(startIndex + pageSize, totalRegistries)} de {totalRegistries} registros
                            </span>
                            <nav>
                                <ul className="pagination mb-0">
                                    <li className={`page-item ${verifiedPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}>Anterior</button>
                                    </li>
                                    {[...Array(totalPages)].map((_, i) => (
                                        <li key={i} className={`page-item ${verifiedPage === i + 1 ? 'active' : ''}`}>
                                            <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${verifiedPage === totalPages ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}>Siguiente</button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>

                {/* 2. Sección colapsable "Pantalla Gigante (Estadio)" */}
                <div className="card mt-4 border border-secondary shadow-sm rounded-3 mb-4">
                    <div 
                        className="card-header bg-dark text-white py-3 px-4 d-flex justify-content-between align-items-center select-none" 
                        onClick={() => setIsStadiumPanelExpanded(!isStadiumPanelExpanded)}
                        style={{ cursor: 'pointer' }}
                    >
                        <h2 className="fs-5 fw-bold mb-0 d-flex align-items-center gap-2">
                            <i className="bi bi-window-desktop text-warning"></i>
                            Pantalla Gigante (Estadio) - Control de Proyección
                        </h2>
                        <i className={`bi fs-5 ${isStadiumPanelExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </div>
                    {isStadiumPanelExpanded && (
                        <div className="card-body bg-light p-4">
                            <div className="row g-4">
                                <div className="col-12 col-md-6 border-end">
                                    <p className="fw-semibold text-secondary small uppercase mb-3">Acciones de Proyección</p>
                                    <div className="d-flex flex-column gap-3 mb-4">
                                        <button 
                                            type="button" 
                                            onClick={showRankingOnScreen} 
                                            className="btn btn-outline-primary py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                        >
                                            <i className="bi bi-list-ol fs-5"></i>
                                            Mostrar Ranking en Pantalla Gigante
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={showQrOnScreen} 
                                            className="btn btn-outline-primary py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                        >
                                            <i className="bi bi-qr-code-scan fs-5"></i>
                                            Mostrar QR en Pantalla Gigante
                                        </button>
                                        <div className="d-flex gap-2">
                                            <button 
                                                type="button" 
                                                onClick={handleShowOverlay} 
                                                className="btn btn-outline-success flex-fill py-2 fw-bold"
                                            >
                                                <i className="bi bi-eye-fill me-1"></i> Forzar Overlay
                                            </button>
                                            <button 
                                                type="button" 
                                                onClick={handleRefreshOverlay} 
                                                className="btn btn-outline-warning text-dark flex-fill py-2 fw-bold"
                                            >
                                                <i className="bi bi-arrow-repeat me-1"></i> Refrescar
                                            </button>
                                            <button 
                                                type="button" 
                                                onClick={handleHideOverlay} 
                                                className="btn btn-outline-danger flex-fill py-2 fw-bold"
                                            >
                                                <i className="bi bi-eye-slash-fill me-1"></i> Ocultar
                                            </button>
                                        </div>
                                    </div>

                                    {/* Mapeo de "Configuración del Juego (Duración)" debajo de proyección */}
                                    <p className="fw-semibold text-secondary small uppercase mb-2">Configuración de Duración</p>
                                    <div className="input-group shadow-sm">
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            value={duration} 
                                            onChange={(e) => setDuration(parseInt(e.target.value) || 0)} 
                                        />
                                        <button onClick={handleConfigGame} className="btn btn-danger fw-bold" type="button">
                                            Guardar Duración
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="col-12 col-md-6">
                                    <p className="fw-semibold text-secondary small uppercase mb-2">Desplazamiento del Overlay (OBS)</p>
                                    <div className="bg-white p-3 rounded border shadow-sm">
                                        <label className="form-label fw-semibold text-dark d-flex justify-content-between mb-2">
                                            <span>Posición Vertical (Offset Y)</span>
                                            <span className="badge bg-danger fs-6">{offsetY} px</span>
                                        </label>
                                        <input 
                                            type="range" 
                                            className="form-range" 
                                            min="0" 
                                            max="500" 
                                            value={offsetY}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                setOffsetY(val);
                                                throttledUpdatePosition(val);
                                            }}
                                        />
                                        <span className="text-muted small d-block mt-1">Regula la posición vertical del cintillo del ranking u overlay de transmisión.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* CONFIGURACIÓN DE REGISTROS AL JUEGO */}
                <div className="col-12 mb-4">
                    <div className="bg-light p-4 rounded-3 border h-100 d-flex flex-column justify-content-between shadow-sm">
                        <div>
                            <p className='fs-4 fw-bold text-center mb-3 text-dark'>Acceso al juego</p>
                            <p className='text-center mb-3'>Controla si los aficionados pueden escanear el QR e inscribirse a la dinámica.</p>
                            <p className='text-center fs-5 text-dark'>
                                Estatus de registro: <span className={`fw-bold ${isGameActive ? 'text-success' : 'text-danger'}`}>{isGameActive ? 'Activo (Abierto)' : 'Inactivo (Cerrado)'}</span>
                            </p>
                        </div>
                        <div className="my-1">
                            <button
                                type="button"
                                onClick={() => triggerToggleActive(!isGameActive)}
                                className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all shadow-sm ${
                                    isGameActive 
                                    ? 'btn-outline-danger' 
                                    : 'btn-outline-success'
                                }`}
                            >
                                <i className={`bi ${isGameActive ? 'bi-toggle-off' : 'bi-toggle-on'}`}></i>
                                {isGameActive ? 'Cerrar registros' : 'Abrir registros'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ZONA DE PELIGRO */}
                <div className="card mt-4 border border-danger shadow-sm rounded-3 mb-4">
                    <div 
                        className="card-header bg-danger text-white py-3 px-4 d-flex justify-content-between align-items-center select-none" 
                        onClick={() => setIsDangerZonePanelExpanded(!isDangerZonePanelExpanded)}
                        style={{ cursor: 'pointer' }}
                    >
                        <h2 className="fs-5 fw-bold mb-0 d-flex align-items-center gap-2">
                            <i className="bi bi-exclamation-triangle-fill"></i> Zona de peligro
                        </h2>
                        <i className={`bi fs-5 ${isDangerZonePanelExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </div>
                    {isDangerZonePanelExpanded && (
                        <div className="card-body bg-light p-4">
                            <div className="row g-4">
                                <div className="col-12">
                                    <p className="text-muted small mb-3">Esto vaciará permanentemente la tabla de participantes y métricas registradas para esta sala o evento.</p>
                                    <button 
                                        type="button" 
                                        onClick={triggerResetGame} 
                                        className="btn btn-outline-danger w-100 fw-bold py-2.5 shadow-sm"
                                    >
                                        <i className="bi bi-trash3-fill me-1"></i>
                                        Reiniciar datos completos de dinámica
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </SecondaryBase>
    );
};

export { PluckingRooster };
export default PluckingRooster;